/**
 * Specchio del contratto backend (TeraWorks.Application.Griglie).
 * I tipi di colonna NON vengono ridichiarati qui a mano per ogni griglia:
 * arrivano da GET /api/griglie/{nome}/colonne, così backend e frontend non possono divergere.
 */

export type TipoColonna =
  | 'Testo'
  | 'Numero'
  | 'Importo'
  | 'Percentuale'
  | 'Data'
  /** Data con l'ora: stessi filtri di `Data` — si sceglie il giorno — ma si mostra l'ora. */
  | 'DataOra'
  | 'Elenco'
  | 'Booleano';

export type OperatoreFiltro =
  | 'Contiene' | 'NonContiene' | 'IniziaCon' | 'Esatto'
  | 'MaggioreDi' | 'MinoreDi' | 'Tra' | 'UgualeA'
  | 'UltimiNGiorni' | 'PeriodoCorrente' | 'PrimaDi' | 'DopoIl'
  | 'EUnoDi' | 'NonEUnoDi'
  | 'Vero' | 'Falso'
  | 'NonValorizzato' | 'Valorizzato';

export type PeriodoCorrente =
  | 'Oggi' | 'Ieri' | 'SettimanaCorrente' | 'MeseCorrente' | 'TrimestreCorrente' | 'AnnoCorrente';

export interface DefinizioneColonna {
  nome: string;
  tipo: TipoColonna;
  ordinabile: boolean;
  sommabile: boolean;
  cercabile: boolean;
  operatori: OperatoreFiltro[];
}

export interface MetadatiGriglia {
  griglia: string;
  ordinamentoPredefinito: { colonna: string; discendente: boolean };
  /**
   * Con che filtri conviene aprire la griglia: li decide il backend, li applica la
   * griglia come filtri attivi. Restano visibili fra i chip e si togliono con un clic —
   * se li applicasse la query non si potrebbe chiedere «fammi vedere anche gli
   * annullati», e i totali di colonna non sarebbero più la somma di quello che si vede.
   */
  filtriPredefiniti?: FiltroColonna[];
  colonne: DefinizioneColonna[];
}

export interface FiltroColonna {
  colonna: string;
  operatore: OperatoreFiltro;
  valori: (string | null)[];
}

export interface Ordinamento {
  colonna: string;
  discendente: boolean;
}

export interface RichiestaGriglia {
  pagina: number;
  dimensione: number;
  filtri: FiltroColonna[];
  ordinamenti: Ordinamento[];
  ricerca?: string;
}

export interface RisultatoGriglia<T = Record<string, unknown>> {
  righe: T[];
  totaleRighe: number;
  pagina: number;
  dimensione: number;
  totali: Record<string, number>;
}

export interface ValoreDistinto {
  valore: string | null;
  occorrenze: number;
}

/** Etichette in italiano per gli operatori: l'utente non legge nomi di enum. */
export const ETICHETTE_OPERATORE: Record<OperatoreFiltro, string> = {
  Contiene: 'contiene',
  NonContiene: 'non contiene',
  IniziaCon: 'inizia con',
  Esatto: 'esattamente',
  MaggioreDi: 'maggiore di',
  MinoreDi: 'minore di',
  Tra: 'compreso tra',
  UgualeA: 'uguale a',
  UltimiNGiorni: 'ultimi n giorni',
  PeriodoCorrente: 'periodo',
  PrimaDi: 'prima del',
  DopoIl: 'dopo il',
  EUnoDi: 'è uno di',
  NonEUnoDi: 'non è',
  Vero: 'sì',
  Falso: 'no',
  NonValorizzato: 'non valorizzato',
  Valorizzato: 'valorizzato',
};

export const ETICHETTE_PERIODO: Record<PeriodoCorrente, string> = {
  Oggi: 'oggi',
  Ieri: 'ieri',
  SettimanaCorrente: 'settimana in corso',
  MeseCorrente: 'mese in corso',
  TrimestreCorrente: 'trimestre in corso',
  AnnoCorrente: 'anno in corso',
};

/** Quanti valori chiede l'operatore. -1 = uno o più. */
export function valoriAttesi(operatore: OperatoreFiltro): number {
  switch (operatore) {
    case 'Tra':
      return 2;
    case 'EUnoDi':
    case 'NonEUnoDi':
      return -1;
    case 'Vero':
    case 'Falso':
    case 'NonValorizzato':
    case 'Valorizzato':
      return 0;
    default:
      return 1;
  }
}

/** Un filtro è pronto per essere applicato solo se ha i valori che il suo operatore richiede. */
export function filtroCompleto(filtro: FiltroColonna): boolean {
  const attesi = valoriAttesi(filtro.operatore);
  const pieni = filtro.valori.filter((v) => v !== null && v !== '').length;
  if (attesi === 0) return true;
  if (attesi === -1) return pieni > 0;
  return pieni === attesi;
}

export const TIPI_NUMERICI: TipoColonna[] = ['Numero', 'Importo', 'Percentuale'];

export const TIPI_TEMPORALI: TipoColonna[] = ['Data', 'DataOra'];

/** Il tipo come lo legge l'utente sotto l'intestazione di colonna: «dataora» non è una parola. */
export function etichettaTipo(tipo: TipoColonna): string {
  return tipo === 'DataOra' ? 'data e ora' : tipo.toLowerCase();
}
